// Copyright (C) Unite Digital LLC - All Rights Reserved.
// Unauthorized copying of this file, via any medium is strictly prohibited.
// Proprietary and confidential.
// Developed by Unite Digital LLC.

import { AfterViewInit, Component, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Deal, DealData, JacketType } from '@interfaces/deal.interfaces';
import { DealsService } from '@services/deals.service';
import { DocumentsService } from '@services/documents.service';
import { UserService } from '@services/user.service';
import { takeWhile } from 'rxjs';
import { DealershipService } from '../../services/dealership.service';
import { User } from '@interfaces/user.interfaces';
import { MatPaginator } from '@angular/material/paginator';
import { AppEvent } from '@interfaces/interfaces';

@Component({
    selector: 'app-pddj-deals',
    templateUrl: './pddj-deals.component.html',
    styleUrls: ['./pddj-deals.component.scss'],
})
export class PddjDealsComponent implements OnInit, OnDestroy, AfterViewInit {
    @ViewChild(MatPaginator) paginator: MatPaginator;
    deals: Deal[] = [];
    selectedDealType = 1;
    public isFiltered = false;
    private isComponentAlive = true;
    public totalNumberOfDeals: number = null;
    public selectedDealership: number = null;
    public selectedTabIndex = 0;
    public isLoading = false;
    public query = '';
    @Input() dealUrl: string[];

    constructor(
        private readonly dealsService: DealsService,
        private readonly dealershipService: DealershipService,
        private readonly documentService: DocumentsService,
        private readonly route: ActivatedRoute,
        private readonly userService: UserService
    ) {}
    ngOnInit(): void {
        this.deals = this.route.snapshot.data.deals.deals;
        this.totalNumberOfDeals = this.route.snapshot.data.deals.count;

        this.dealershipChangedReloadDeals();
    }

    ngOnDestroy(): void {
        this.isComponentAlive = false;
    }

    ngAfterViewInit(): void {
        this.dealershipService.dealershipUpdate$.pipe(takeWhile(() => this.isComponentAlive)).subscribe(() => {
            this.dealsService
                .getDealsByType$(JacketType.Deal)
                .pipe(takeWhile(() => this.isComponentAlive))
                .subscribe((deals) => (this.deals = deals.deals));
        });
    }

    public onPageChange(event: any): void {
        const offset = this.paginator.pageIndex * this.paginator.pageSize;
        const pageSize = this.paginator.pageSize;
        const query: string = this.query;
        this.selectDealType(this.selectedTabIndex, query, pageSize, offset);
    }

    private dealershipChangedReloadDeals(): void {
        this.userService
            .user$()
            .pipe(takeWhile(() => this.isComponentAlive))
            .subscribe((user: User) => {
                if (this.selectedDealership !== user.dealerId) {
                    this.isLoading = true;
                    this.selectedDealership = user.dealerId;
                    this.getDeals();
                }
            });
    }

    private getDeals(): void {
        this.dealsService
            .getDealsByType$(JacketType.Deal)
            .pipe(takeWhile(() => this.isComponentAlive))
            .subscribe((deals: DealData) => {
                this.deals = deals.deals;
                this.totalNumberOfDeals = deals.count;
                this.selectedTabIndex = 0;
                this.isLoading = false;
            });
    }

    selectDealType(selectedTabIndex: number, query?: string, pageSize?: number, offset?: number) {
        this.selectedDealType = selectedTabIndex;
        this.isLoading = true;
        // DealStatuses start at 1, not 0, so we add one to the tab index to match DealStatus values
        this.dealsService
            .getDeals$([selectedTabIndex + 1], query, pageSize, offset, JacketType.Deal)
            .pipe(takeWhile(() => this.isComponentAlive))
            .subscribe((deals) => {
                // scrollIntoView('#query-form');
                this.deals = deals.deals;
                this.totalNumberOfDeals = deals.count;
                this.isLoading = false;
            });
    }

    setText(): string {
        return this.documentService.pddjQueueVerbiage();
    }

    public eventHandler(event: AppEvent): void {
        switch (event.context) {
            case 'search':
            case 'query':
                this.query = event.data;
                this.searchDeals(event.data);
                break;
            default:
                break;
        }
    }

    searchDeals(queryValue?: string) {
        // paginator only exists on page if deals.length > 20
        const offset = this.paginator ? this.paginator.pageIndex * this.paginator.pageSize : 0;
        const pageSize = this.paginator ? this.paginator.pageSize : 20;
        this.isFiltered = queryValue.length ? true : false;

        this.dealsService
            .getDeals$([this.selectedTabIndex + 1], queryValue, pageSize, offset, JacketType.Deal)
            .pipe(takeWhile(() => this.isComponentAlive))
            .subscribe((deals) => {
                this.deals = deals.deals;
                this.totalNumberOfDeals = deals.count;
            });
    }
}
